import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";

import Card from "../../../shared/components/Card";
import Button from "../../../shared/components/Button";

import Form from "../../../shared/components/Form";

import WorkOrderSideMenu from "../components/WorkOrderSideMenu";
import {
  getWorkOrderById,
  createTimeLog,
  updateWorkOrderState,
  patchWorkOrder,
} from "../../../../../services/Production/workOrderClient";
import { AsyncSelectSearch } from "../../../shared/components/SelectSearch";
import { getWorkstations } from "../../../../../services/Production/workstationClient";
import { ExtraInfoDropdownItem } from "../../../shared/components/Dropdown";
import { getBadgeVariant } from "../../../shared/utils";
import Badge from "../../../shared/components/Badge";
import ConsumeProductsModal from "../components/ConsumeProductsModal";
import TimeLogsTable from "../components/TimeLogsTable";
import TimeLogger from "../components/TimeLogger";
import TimeLoggerProvider from "../components/TimeLoggerContext";
import useLoadingToaster from "../../../shared/hooks/useLoadingToaster";

export default function WorkOrderDetailsPage() {
  const [showConsumeProductsModal, setShowProductsModal] = useState(false);
  const updatedStateRef = useRef(null);

  const { id } = useParams();
  const queryClient = useQueryClient();
  const { data, error, isLoading, refetch } = useQuery(
    ["WorkOrders", id],
    getWorkOrderById
  );

  const {
    mutate,
    isLoading: isSaveLogsLoading,
    isError: isSaveLogsError,
    isSuccess: isSaveLogsSuccess,
  } = useMutation({
    mutationFn: createTimeLog,
  });

  const {
    mutate: updateWorkOrderMutation,
    isError: isUpdateWorkOrderStateError,
    isLoading: isUpdateWorkOrderStateLoading,
    isSuccess: isUpdateWorkOrderStateSuccess,
  } = useMutation({
    mutationFn: patchWorkOrder,
    onSuccess: () => refetch(),
    // queryClient.setQueryData(["WorkOrders", id], (oldData) => {
    //   return { ...oldData, state: updatedStateRef.current };
    // }),
  });

  const {
    control,
    formState: { errors },
    reset,
  } = useForm();

  useLoadingToaster({
    state: {
      isError: isSaveLogsError,
      isLoading: isSaveLogsLoading,
      isSuccess: isSaveLogsSuccess,
    },
    renderMessages: {
      success: "Added with success",
      error: "Error Adding time log:'(",
      loading: "Adding time log",
    },
  });

  useLoadingToaster({
    state: {
      isError: isUpdateWorkOrderStateError,
      isLoading: isUpdateWorkOrderStateLoading,
      isSuccess: isUpdateWorkOrderStateSuccess,
    },
    renderMessages: {
      success: "Work order updated with success",
      error: "Error updating work order",
      loading: "Updating work order",
    },
  });

  useEffect(() => {
    if (data) reset({ workstation: data?.operationDetails?.workstation });
  }, [data, reset]);

  const onAddTimeLogHandler = (data) => mutate({ workOrderId: id, data });

  const onStartTimerHandler = () => {
    if (data.state === "STARTED" || data.state === "FINISHED") return;
    updateWorkOrderMutation({
      workOrderId: id,
      data: [
        { op: "replace", path: "/state", value: "STARTED" },
        {
          op: "replace",
          path: "/actualStartDate",
          value: new Date(Date.now()).toISOString(),
        },
      ],
    });
  };

  const onFinishedHandler = () => {
    if (data.state === "FINISHED") return;
    updateWorkOrderMutation({
      workOrderId: id,
      data: [
        { op: "replace", path: "/state", value: "FINISHED" },
        {
          op: "replace",
          path: "/actualEndDate",
          value: new Date(Date.now()).toISOString(),
        },
      ],
    });
  };

  if (isLoading) return <h1>Loading Placeholder ....</h1>;
  if (error) return <h1>Failed to load work order</h1>;

  return (
    data && (
      <>
        {/* Select consumed products with their quantities */}
        <ConsumeProductsModal
          setIsShown={setShowProductsModal}
          isShown={showConsumeProductsModal}
          productionOrderId={data.productionOrder.id}
          products={data.productionOrder.billOfMaterials.componentList.map(
            ({ product }) => product
          )}
        />
        <TimeLoggerProvider
          workOrderId={id}
          onAddTimeLog={onAddTimeLogHandler}
          onStartTimeLog={onStartTimerHandler}
        >
          <Card className="mt-2">
            <Card.Header>
              <div style={{ alignItems: "center" }} className="row">
                <h4>
                  Bon de travail /
                  <small>
                    [CODE]
                    <Badge
                      className="ml-2"
                      variant={getBadgeVariant(data.state)}
                      text={data.state}
                    />
                  </small>
                </h4>
                <TimeLogger />
                <Button
                  onClick={() => setShowProductsModal(true)}
                  className="ml-4 mr-2"
                  variant="outline"
                >
                  Transfer / Consume
                </Button>
                <Button onClick={onFinishedHandler}>Terminer</Button>
              </div>
            </Card.Header>
          </Card>
          <div className="row">
            <div className="col-3 ">
              <Card style={{ minHeight: "81%" }}>
                <WorkOrderSideMenu files={data.operationDetails.files || []} />
              </Card>
            </div>
            <div className="col-9">
              <Card>
                <Card.Header>
                  <Card.Title title="Détails" />
                </Card.Header>
                <Card.Body>
                  <Form>
                    <Form.Group>
                      <label htmlFor="#">Station de travail</label>
                      <Controller
                        rules={{ required: true }}
                        name={`workstation`}
                        control={control}
                        render={({ field }) => (
                          <AsyncSelectSearch
                            isInvalid={!!errors.workstation}
                            field={field}
                            loaderFn={getWorkstations}
                            resourceName={"Workstations"}
                            onSelect={(option) => {
                              console.warn(option);
                            }}
                            getSelectValue={(option) => option.code}
                            renderItem={(option) => (
                              <ExtraInfoDropdownItem
                                key={option.id}
                                option={{
                                  main: option.code,
                                  info: option.name,
                                }}
                              />
                            )}
                          />
                        )}
                      />
                    </Form.Group>
                    <Form.Group>
                      <label htmlFor="#">Employee</label>
                    </Form.Group>
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>
                  <Card.Title title="Time logs" />
                </Card.Header>
                <Card.Body>
                  <TimeLogsTable />
                </Card.Body>
              </Card>
            </div>
          </div>
        </TimeLoggerProvider>
      </>
    )
  );
}
